/**
 * Money, held as integers.
 *
 * Every amount in this system is paise or whole rupees, never a floating-point number. 0.1 + 0.2
 * is not 0.3 in binary floating point, and a payment advice that is a paisa out is a document
 * somebody has to argue about. Integers cannot drift.
 *
 * Division is the only place precision can be lost, so it happens once, at the end, with the
 * rounding rule stated: multiply first, divide last, round half up.
 */

/** Half-up division of non-negative integers. `roundDiv(3001, 30)` is 100, `roundDiv(45, 30)` is 2. */
export function roundDiv(numerator: number, denominator: number): number {
  if (denominator <= 0) throw new Error('roundDiv: denominator must be positive');
  if (numerator < 0) throw new Error('roundDiv: this system has no negative money');
  return Math.floor((numerator + Math.floor(denominator / 2)) / denominator);
}

/** Paise to whole rupees, half up. 15049 paise is ₹150; 15050 is ₹151. */
export const paiseToRupees = (paise: number): number => roundDiv(paise, 100);

export const rupeesToPaise = (rupees: number): number => Math.round(rupees * 100);

/** "₹29,700" — Indian digit grouping (last three, then pairs), no decimals. */
export function formatRupees(rupees: number): string {
  const s = String(Math.abs(Math.trunc(rupees)));
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}` : last3;
  return `${rupees < 0 ? '-' : ''}₹${grouped}`;
}

/** "₹1,000.33" — for a rate, which is a division and is shown to the paisa so it can be checked. */
export function formatPaise(paise: number): string {
  const rupees = Math.trunc(paise / 100);
  const p = Math.abs(paise % 100);
  return `${formatRupees(rupees)}.${String(p).padStart(2, '0')}`;
}

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

/** 0–99. */
function twoDigits(n: number): string {
  if (n < 20) return ONES[n]!;
  const t = TENS[Math.floor(n / 10)]!;
  const o = ONES[n % 10]!;
  return o ? `${t} ${o}` : t;
}

/** 0–999. */
function threeDigits(n: number): string {
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const parts: string[] = [];
  if (h) parts.push(`${ONES[h]} Hundred`);
  if (rest) parts.push(twoDigits(rest));
  return parts.join(' ');
}

/**
 * "Rupees Twenty Nine Thousand Seven Hundred Only" — Indian grouping, because the document is read
 * in India and "twenty-nine thousand seven hundred" is not how a lakh is spoken.
 *
 * Printed under the net on every payment advice: a figure in words is the oldest defence there is
 * against a digit being changed after the fact.
 */
export function rupeesInWords(rupees: number): string {
  const n = Math.trunc(Math.abs(rupees));
  if (n === 0) return 'Rupees Zero Only';

  const crore = Math.floor(n / 10_000_000);
  const lakh = Math.floor((n % 10_000_000) / 100_000);
  const thousand = Math.floor((n % 100_000) / 1_000);
  const rest = n % 1_000;

  const parts: string[] = [];
  if (crore) parts.push(`${threeDigits(crore)} Crore`);
  if (lakh) parts.push(`${threeDigits(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigits(thousand)} Thousand`);
  if (rest) parts.push(threeDigits(rest));

  return `Rupees ${parts.join(' ')} Only`;
}
